import React, { useState, useCallback } from "react";
import { BRANDS } from "../../utils/constants";
import useCSVProcessor from "../../hooks/useCSVProcessor";
import useSupabaseData from "../../hooks/useSupabaseData";
import {
  UploadCloud,
  CheckCircle,
  AlertTriangle,
  Database,
  Cog,
  Zap,
} from "lucide-react";

import Header from "../Header";
import Card from "../ui/Card";
import FileUploader from "../FileUploader";
import ProgressBar from "../ui/ProgressBar";

const CsvUploadPage = () => {
  const {
    isLoading: supabaseLoading,
    error: supabaseError,
    success: supabaseSuccess,
    importProgress,
    importMessage,
    importCSVDataInNewDashboard,
    clearMessages: clearSupabaseMessages,
  } = useSupabaseData();

  const {
    isLoading: csvLoading,
    error: csvError,
    success: csvSuccess,
    processFile,
    clearMessages,
  } = useCSVProcessor();

  const [brandForCSVUpload, setBrandForCSVUpload] = useState("none");
  const [uploadInitiated, setUploadInitiated] = useState(false);

  const handleFileUpload = useCallback(
    async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      setUploadInitiated(true);
      clearMessages();
      clearSupabaseMessages();

      try {
        await new Promise((resolve, reject) => {
          processFile(file, async (csvData) => {
            try {
              await importCSVDataInNewDashboard(csvData, brandForCSVUpload);
              resolve();
            } catch (err) {
              reject(err);
            }
          });
        });
      } catch (err) {
        console.error("Error during file processing or upload:", err);
      }
    },
    [
      processFile,
      importCSVDataInNewDashboard,
      brandForCSVUpload,
      clearMessages,
      clearSupabaseMessages,
    ]
  );

  const isLoading = csvLoading || supabaseLoading;
  const error = csvError || supabaseError;
  const success = csvSuccess || supabaseSuccess;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white">
      <Header />
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center">
        <div className="w-full max-w-4xl mx-auto">
          <Card className="p-1 bg-gradient-to-br from-white/10 to-transparent backdrop-blur-2xl shadow-2xl rounded-2xl">
            <div className="bg-black/80 rounded-xl p-6 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                {/* Left side: Instructions and Brand Selector */}
                <div className="space-y-6">
                  <h3 className="text-3xl font-bold text-white">
                    Upload Performance CSV
                  </h3>
                  <p className="text-gray-300">
                    Select a brand and then upload the corresponding CSV file.
                    The data will be processed and added to the existing
                    database.
                  </p>
                  <div className="space-y-2">
                    <label
                      htmlFor="brand-select-csv"
                      className="block text-sm font-semibold text-gray-300"
                    >
                      1. Select Brand
                    </label>
                    <select
                      id="brand-select-csv"
                      value={brandForCSVUpload}
                      onChange={(e) => setBrandForCSVUpload(e.target.value)}
                      className="w-full bg-black/50 border-2 border-white/20 rounded-lg py-3 px-4 text-white focus:ring-2 focus:ring-[#B8460E] focus:border-[#B8460E] appearance-none"
                      disabled={isLoading}
                    >
                      <option value="none">Select a brand...</option>
                      {BRANDS.map((brand) => (
                        <option key={brand.value} value={brand.value}>
                          {brand.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right side: File Uploader */}
                <div className="space-y-4 rounded-xl bg-white/5 p-6 border border-white/10">
                  <div className="text-center">
                    <h4 className="text-lg font-semibold text-white mb-4">
                      2. Choose CSV File
                    </h4>
                    <FileUploader
                      onFileUpload={handleFileUpload}
                      isLoading={isLoading}
                      disabled={brandForCSVUpload === "none" || isLoading}
                    />
                    {brandForCSVUpload === "none" && !isLoading && (
                      <p className="text-xs text-amber-400 mt-4">
                        Please select a brand before uploading a CSV.
                      </p>
                    )}
                  </div>

                  <div className="flex justify-around pt-4">
                    <div className="text-center">
                      <Database className="h-6 w-6 mx-auto text-green-400" />
                      <p className="text-sm font-medium mt-1">Append Data</p>
                      <p className="text-xs text-gray-400">
                        Adds to existing records
                      </p>
                    </div>
                    <div className="text-center">
                      <Cog className="h-6 w-6 mx-auto text-blue-400" />
                      <p className="text-sm font-medium mt-1">
                        Auto Processing
                      </p>
                      <p className="text-xs text-gray-400">
                        Affiliate detection
                      </p>
                    </div>
                    <div className="text-center">
                      <Zap className="h-6 w-6 mx-auto text-purple-400" />
                      <p className="text-sm font-medium mt-1">Instant Access</p>
                      <p className="text-xs text-gray-400">
                        Data available immediately
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Progress Bar */}
              {isLoading && uploadInitiated && (
                <div className="mt-6 p-4 bg-black/20 rounded-lg border border-white/10">
                  <ProgressBar
                    progress={importProgress}
                    status={
                      error ? "error" : success ? "success" : "processing"
                    }
                    message={importMessage}
                    showPercentage={true}
                  />
                </div>
              )}

              {uploadInitiated && !isLoading && (
                <div className="mt-6">
                  {success && (
                    <div className="flex items-center gap-3 text-green-400 bg-green-500/10 p-3 rounded-lg">
                      <CheckCircle className="h-6 w-6" />
                      <div>
                        <p className="font-semibold">Upload Successful!</p>
                        <p className="text-sm">{success}</p>
                      </div>
                    </div>
                  )}
                  {error && (
                    <div className="flex items-center gap-3 text-red-400 bg-red-500/10 p-3 rounded-lg">
                      <AlertTriangle className="h-6 w-6" />
                      <div>
                        <p className="font-semibold">Upload Failed</p>
                        <p className="text-sm">{error}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CsvUploadPage;
